import {
  createContext, useCallback, useContext, useEffect, useId, useRef, useState,
  type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from 'react';
import { initials } from '@plataforma/shared';
import { ApiError } from '../lib/api';
import { t } from '../lib/i18n';

// ---------------- Botones ----------------
type BtnVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
export function Button({
  variant = 'secondary', size, loading, children, className = '', ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: BtnVariant; size?: 'sm' | 'lg'; loading?: boolean }) {
  return (
    <button
      type="button"
      {...rest}
      disabled={rest.disabled || loading}
      className={`btn btn-${variant} ${size ? `btn-${size}` : ''} ${className}`}
    >
      {loading && <span className="spinner" aria-hidden />}
      {children}
    </button>
  );
}

// ---------------- Campos ----------------
export function Field({ label, hint, error, children, htmlFor }: { label?: ReactNode; hint?: ReactNode; error?: string; children: ReactNode; htmlFor?: string }) {
  return (
    <div className="field">
      {label && <label htmlFor={htmlFor}>{label}</label>}
      {children}
      {error ? <div className="error" role="alert">{error}</div> : hint ? <div className="hint">{hint}</div> : null}
    </div>
  );
}

type InputProps = InputHTMLAttributes<HTMLInputElement> & { label?: ReactNode; hint?: ReactNode; error?: string };
export function TextInput({ label, hint, error, className = '', id, ...rest }: InputProps) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <Field label={label} hint={hint} error={error} htmlFor={fid}>
      <input id={fid} {...rest} className={`input ${error ? 'invalid' : ''} ${className}`} aria-invalid={!!error} />
    </Field>
  );
}

export function TextArea({ label, hint, error, id, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: ReactNode; hint?: ReactNode; error?: string }) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <Field label={label} hint={hint} error={error} htmlFor={fid}>
      <textarea id={fid} {...rest} className={`textarea ${error ? 'invalid' : ''}`} aria-invalid={!!error} />
    </Field>
  );
}

export function SelectInput({ label, hint, error, id, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { label?: ReactNode; hint?: ReactNode; error?: string }) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <Field label={label} hint={hint} error={error} htmlFor={fid}>
      <select id={fid} {...rest} className={`select ${error ? 'invalid' : ''}`} aria-invalid={!!error}>
        {children}
      </select>
    </Field>
  );
}

export function Checkbox({ label, checked, onChange, disabled }: { label: ReactNode; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <label className="check">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

// ---------------- Estructura ----------------
export function Card({ title, actions, children, footer, flush, className = '' }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; footer?: ReactNode; flush?: boolean; className?: string }) {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        <div className="card-header">
          {typeof title === 'string' ? <h3>{title}</h3> : <div className="grow">{title}</div>}
          {actions}
        </div>
      )}
      <div className={`card-body ${flush ? 'flush' : ''}`}>{children}</div>
      {footer && <div className="card-footer">{footer}</div>}
    </section>
  );
}

export function PageHeader({ title, subtitle, crumbs, actions }: { title: ReactNode; subtitle?: ReactNode; crumbs?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="page-header">
      <div className="titles">
        {crumbs && <div className="crumbs">{crumbs}</div>}
        <h1>{title}</h1>
        {subtitle && <p className="muted" style={{ marginTop: 4 }}>{subtitle}</p>}
      </div>
      {actions && <div className="row wrap">{actions}</div>}
    </div>
  );
}

export type Tone = 'neutral' | 'green' | 'amber' | 'red' | 'blue' | 'violet';
export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return <span className={`badge ${tone === 'neutral' ? '' : tone}`}>{children}</span>;
}

export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: { id: T; label: ReactNode }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t) => (
        <button key={t.id} role="tab" aria-selected={value === t.id} className={`tab ${value === t.id ? 'active' : ''}`} onClick={() => onChange(t.id)}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function Avatar({ first, last, size }: { first: string; last: string; size?: 'lg' }) {
  return <span className={`avatar ${size ?? ''}`} aria-hidden>{initials(first, last)}</span>;
}

export function Empty({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {children && <p style={{ marginBottom: action ? 14 : 0 }}>{children}</p>}
      {action}
    </div>
  );
}

export function Loading({ label = t('Cargando…') }: { label?: string }) {
  return (
    <div className="loading-block" role="status">
      <div className="row"><span className="spinner" /> {label}</div>
    </div>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  if (!error) return null;
  const msg = error instanceof ApiError ? error.message : error instanceof Error ? error.message : t('Ocurrió un error');
  return <div className="alert error" role="alert">{msg}</div>;
}

export function Alert({ tone = 'info', children }: { tone?: 'info' | 'warn' | 'error' | 'success'; children: ReactNode }) {
  return <div className={`alert ${tone}`}>{children}</div>;
}

// ---------------- Modal ----------------
export function Modal({ title, open, onClose, children, footer, wide }: { title: ReactNode; open: boolean; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prev = document.activeElement as HTMLElement | null;
    // Primero el primer campo del formulario; si no hay, el primer botón del contenido (no el de cerrar).
    setTimeout(() => (ref.current?.querySelector<HTMLElement>('.modal-body input, .modal-body select, .modal-body textarea') ?? ref.current?.querySelector<HTMLElement>('.modal-body button, .modal-footer button'))?.focus(), 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true" ref={ref}>
        <div className="modal-header">
          <h2>{title}</h2>
          <Button variant="ghost" className="btn-icon" aria-label={t('Cerrar')} onClick={onClose}>✕</Button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

// ---------------- Avisos ----------------
type ToastItem = { id: number; text: string; tone: 'info' | 'error' | 'success' };
const ToastCtx = createContext<(text: string, tone?: ToastItem['tone']) => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((text: string, tone: ToastItem['tone'] = 'info') => {
    const id = Date.now() + Math.random();
    setItems((x) => [...x, { id, text, tone }]);
    setTimeout(() => setItems((x) => x.filter((t) => t.id !== id)), tone === 'error' ? 6000 : 3500);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" aria-live="polite">
        {items.map((t) => <div key={t.id} className={`toast ${t.tone}`}>{t.text}</div>)}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const push = useContext(ToastCtx);
  return {
    info: (t: string) => push(t, 'info'),
    success: (t: string) => push(t, 'success'),
    error: (e: unknown) => push(e instanceof ApiError || e instanceof Error ? e.message : String(e), 'error'),
  };
}

/** Botón que pide confirmación antes de una acción destructiva. */
export function ConfirmButton({ onConfirm, children, message = t('¿Seguro?'), variant = 'danger', size }: { onConfirm: () => void | Promise<void>; children: ReactNode; message?: string; variant?: BtnVariant; size?: 'sm' | 'lg' }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <>
      <Button variant={variant === 'danger' ? 'ghost' : variant} size={size} onClick={() => setOpen(true)}>{children}</Button>
      <Modal
        title={t('Confirmar')}
        open={open}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>{t('Cancelar')}</Button>
            <Button variant={variant} loading={busy} onClick={async () => { setBusy(true); try { await onConfirm(); setOpen(false); } finally { setBusy(false); } }}>
              {t('Confirmar')}
            </Button>
          </>
        }
      >
        <p>{message}</p>
      </Modal>
    </>
  );
}

export function Pagination({ page, pages, onChange }: { page: number; pages: number; onChange: (p: number) => void }) {
  if (pages <= 1) return null;
  return (
    <div className="row" style={{ justifyContent: 'flex-end', padding: '10px 0' }}>
      <Button size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>{t('Anterior')}</Button>
      <span className="muted small">{t('Página {page} de {pages}', { page, pages })}</span>
      <Button size="sm" disabled={page >= pages} onClick={() => onChange(page + 1)}>{t('Siguiente')}</Button>
    </div>
  );
}

export function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="stat">
      <span className="label">{label}</span>
      <span className="value">{value}</span>
      {sub && <span className="small muted">{sub}</span>}
    </div>
  );
}

/** Valor que se actualiza tras una pausa al escribir. */
export function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
